import * as THREE from 'three';

/**
 * 场景初始化模块
 * 负责创建 Three.js 场景、相机、渲染器三大核心对象
 */
export class Scene {
  /** @type {THREE.Scene} */
  scene;
  /** @type {THREE.PerspectiveCamera} */
  camera;
  /** @type {THREE.Points | null} */
  #particles = null;
  /** @type {THREE.LineSegments | null} */
  #constellation = null;
  /** @type {THREE.Mesh | null} */
  #gridFloor = null;
  /** @type {THREE.Group | null} */
  #energyRings = null;

  constructor() {
    this.scene = new THREE.Scene();
    this.camera = this.#createCamera();
    this.#setupBackground();
    this.#createGridFloor();
    this.#createParticles();
    this.#createConstellation();
    this.#createEnergyRings();
  }

  /**
   * 创建透视相机
   * @returns {THREE.PerspectiveCamera}
   */
  #createCamera() {
    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    camera.position.set(0, 1.5, 5);
    return camera;
  }

  /** 设置场景背景色和雾化效果 */
  #setupBackground() {
    this.scene.background = new THREE.Color('#06060f');
    this.scene.fog = new THREE.FogExp2('#06060f', 0.03);
  }

  /** 创建全息网格地面（程序化 Canvas 纹理） */
  #createGridFloor() {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // 透明背景 + 网格线
    ctx.clearRect(0, 0, 512, 512);
    ctx.strokeStyle = 'rgba(79, 195, 247, 0.15)';
    ctx.lineWidth = 1;

    const divisions = 20;
    const step = 512 / divisions;
    for (let i = 0; i <= divisions; i++) {
      ctx.beginPath();
      ctx.moveTo(i * step, 0);
      ctx.lineTo(i * step, 512);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * step);
      ctx.lineTo(512, i * step);
      ctx.stroke();
    }

    // 径向渐变覆盖（中心亮边缘暗）
    const grad = ctx.createRadialGradient(256, 256, 0, 256, 256, 360);
    grad.addColorStop(0, 'rgba(79, 195, 247, 0.08)');
    grad.addColorStop(0.5, 'rgba(124, 92, 255, 0.03)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 512);

    // 中心十字高光
    ctx.strokeStyle = 'rgba(79, 195, 247, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(256, 0);
    ctx.lineTo(256, 512);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, 256);
    ctx.lineTo(512, 256);
    ctx.stroke();

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 4);

    const geo = new THREE.PlaneGeometry(40, 40);
    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.#gridFloor = new THREE.Mesh(geo, mat);
    this.#gridFloor.rotation.x = -Math.PI / 2;
    this.#gridFloor.position.y = -0.01;
    this.scene.add(this.#gridFloor);
  }

  /** 创建科幻粒子效果（多色漂浮光点） */
  #createParticles() {
    const count = 800;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const spread = 50;

    const colorPalette = [
      [0.31, 0.76, 0.97], // cyan #4fc3f7
      [0.49, 0.36, 1.0],  // purple #7c5cff
      [0.51, 0.83, 0.98], // light cyan #81d4fa
      [0.78, 0.78, 1.0],  // pale blue
      [1.0, 1.0, 1.0],    // white
    ];

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * spread;
      positions[i3 + 1] = (Math.random() - 0.3) * spread * 0.7;
      positions[i3 + 2] = (Math.random() - 0.5) * spread;

      const c = colorPalette[Math.floor(Math.random() * colorPalette.length)];
      colors[i3] = c[0];
      colors[i3 + 1] = c[1];
      colors[i3 + 2] = c[2];

      sizes[i] = Math.random() * 2 + 0.3;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({
      size: 0.06,
      transparent: true,
      opacity: 0.5,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true,
    });

    this.#particles = new THREE.Points(geo, mat);
    this.scene.add(this.#particles);
  }

  /** 创建星座连线（近粒子之间的连接线） */
  #createConstellation() {
    const count = 60;
    const positions = new Float32Array(count * 3);
    const spread = 20;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * spread;
      positions[i3 + 1] = Math.random() * 8 - 1;
      positions[i3 + 2] = (Math.random() - 0.5) * spread;
    }

    // 计算近粒子连线
    const linePositions = [];
    const maxDist = 5;
    for (let i = 0; i < count; i++) {
      for (let j = i + 1; j < count; j++) {
        const dx = positions[i * 3] - positions[j * 3];
        const dy = positions[i * 3 + 1] - positions[j * 3 + 1];
        const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < maxDist) {
          linePositions.push(
            positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2],
            positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2],
          );
        }
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));

    const mat = new THREE.LineBasicMaterial({
      color: new THREE.Color('#4fc3f7'),
      transparent: true,
      opacity: 0.06,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.#constellation = new THREE.LineSegments(geo, mat);
    this.scene.add(this.#constellation);
  }

  /** 创建能量光环（多层旋转圆环） */
  #createEnergyRings() {
    this.#energyRings = new THREE.Group();

    const ringConfigs = [
      { radius: 3, color: '#4fc3f7', opacity: 0.12, speed: 0.15, y: 0 },
      { radius: 4.5, color: '#7c5cff', opacity: 0.08, speed: -0.1, y: -0.5 },
      { radius: 6, color: '#4fc3f7', opacity: 0.05, speed: 0.08, y: 0 },
    ];

    for (const cfg of ringConfigs) {
      const curve = new THREE.EllipseCurve(0, 0, cfg.radius, cfg.radius, 0, 2 * Math.PI, false, 0);
      const points = curve.getPoints(128);
      const geo = new THREE.BufferGeometry().setFromPoints(points);
      const mat = new THREE.LineBasicMaterial({
        color: new THREE.Color(cfg.color),
        transparent: true,
        opacity: cfg.opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const ring = new THREE.Line(geo, mat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = cfg.y;
      ring.userData.speed = cfg.speed;
      this.#energyRings.add(ring);
    }

    this.scene.add(this.#energyRings);
  }

  /** 每帧更新粒子位置（缓慢漂浮） */
  updateParticles(delta) {
    if (!this.#particles) return;
    const pos = this.#particles.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const i3 = i * 3;
      pos.array[i3 + 1] += delta * 0.02;
      pos.array[i3] += Math.sin(Date.now() * 0.001 + i) * 0.001;
      // 飘到顶部后重置到底部
      if (pos.array[i3 + 1] > 15) {
        pos.array[i3 + 1] = -15;
      }
    }
    pos.needsUpdate = true;
    this.#particles.rotation.y += delta * 0.008;

    // 星座连线缓慢旋转
    if (this.#constellation) {
      this.#constellation.rotation.y += delta * 0.005;
    }

    // 能量环旋转
    if (this.#energyRings) {
      for (const ring of this.#energyRings.children) {
        ring.rotation.z += delta * ring.userData.speed;
      }
    }
  }

  /**
   * 窗口尺寸变化时，同步更新相机宽高比和投影矩阵
   * @param {number} width
   * @param {number} height
   */
  onResize(width, height) {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
}
